import { useLayoutEffect, useState } from 'react';

/**
 * Works out which way an absolutely-positioned dropdown panel should open, and how much room it
 * actually has.
 *
 * Every popover in this app is `absolute` inside a `relative` wrapper rather than portalled, which
 * keeps outside-click handling simple but means the panel is blind twice over: it doesn't know
 * where the viewport edge is, and it doesn't know that the card or modal body it sits in will clip
 * it. A dropdown at the foot of a scrolling modal body opens straight into the clipped-off region
 * and reads as broken.
 *
 * So: measure the anchor against the viewport AND against every clipping ancestor, then report
 * which direction has room. Callers decide what to do with `space` — most clamp their scroll list
 * to it, the date picker (a fixed-size calendar) just uses `dropUp`.
 *
 *   const { dropUp, space } = useDropdownPlacement(open, wrapperRef, { wantHeight: 285 });
 *
 * @param open        whether the panel is currently rendered
 * @param anchorRef   ref to the `relative` wrapper the panel is positioned against
 * @param wantHeight  the panel's ideal full height in px
 * @param minHeight   height below which flipping beats shrinking (defaults to wantHeight, i.e. a
 *                    fixed-size panel that can't shrink at all)
 * @returns { dropUp, space } — `space` is the px available in the chosen direction
 */
const EDGE_GAP = 12; // breathing room against the viewport / container edge

const CLIPS = /(auto|scroll|hidden|clip)/;

export default function useDropdownPlacement(
  open,
  anchorRef,
  { wantHeight, minHeight, ignoreClippers = false } = {},
) {
  // `rect` is returned so a panel rendered in a portal can position itself against the anchor it is
  // no longer a descendant of.
  const [placement, setPlacement] = useState({ dropUp: false, space: wantHeight, rect: null });
  const floor = minHeight ?? wantHeight;

  useLayoutEffect(() => {
    if (!open) return undefined;

    // The ancestor chain can't change while the panel is open, so resolve the clipping elements
    // once and only re-read their rects as things scroll.
    // A panel rendered into a portal is not a descendant of these, so nothing between the anchor and
    // the body can clip it — only the viewport bounds it. Walking them anyway would shrink the panel
    // to a container it is no longer inside, which is what boxed an eight-option list into the four
    // rows that fitted inside a short modal.
    const clippers = [];
    if (!ignoreClippers) {
      for (let node = anchorRef.current?.parentElement; node && node !== document.body; node = node.parentElement) {
        const cs = window.getComputedStyle(node);
        if (CLIPS.test(cs.overflowY + cs.overflowX)) clippers.push(node);
      }
    }

    const measure = () => {
      const rect = anchorRef.current?.getBoundingClientRect();
      if (!rect) return;
      let ceiling = EDGE_GAP;
      let ground = window.innerHeight - EDGE_GAP;
      clippers.forEach((c) => {
        const r = c.getBoundingClientRect();
        ceiling = Math.max(ceiling, r.top);
        ground = Math.min(ground, r.bottom);
      });
      const below = ground - rect.bottom;
      const above = rect.top - ceiling;
      // Only flip when going up genuinely buys room; a slightly cramped downward list still reads
      // better than one that covers the field you just clicked.
      const dropUp = below < Math.min(wantHeight, floor) && above > below;
      setPlacement({
        dropUp,
        space: dropUp ? above : below,
        rect: { top: rect.top, bottom: rect.bottom, left: rect.left, width: rect.width },
      });
    };

    measure();
    window.addEventListener('resize', measure);
    // Capture phase, because the app shell scrolls #app-main and modals scroll their own body —
    // neither fires a scroll event on window.
    document.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      document.removeEventListener('scroll', measure, true);
    };
  }, [open, anchorRef, wantHeight, floor, ignoreClippers]);

  return placement;
}
